import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const AuthContext = createContext(null);

const API = process.env.REACT_APP_BACKEND_URL;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);

  const checkAuth = useCallback(async () => {
    // First check if setup is needed
    try {
      const setupRes = await fetch(`${API}/api/auth/setup-status`);
      if (setupRes.ok) {
        const setupData = await setupRes.json();
        if (setupData.needs_setup) {
          setNeedsSetup(true);
          setLoading(false);
          return;
        }
      }
    } catch {}
    setNeedsSetup(false);

    const token = localStorage.getItem('greencad_token');
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    try {
      const res = await fetch(`${API}/api/auth/me`, { credentials: 'include', headers });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
      } else {
        localStorage.removeItem('greencad_token');
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const loginStep1 = useCallback(async (email, password) => {
    const res = await fetch(`${API}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const detail = err.detail;
      const e = new Error(typeof detail === 'object' ? detail.message : (detail || 'Login failed'));
      e.detail = detail;
      e.status = res.status;
      throw e;
    }
    return await res.json();
  }, []);

  const setup2FA = useCallback(async (tempToken) => {
    const res = await fetch(`${API}/api/auth/2fa/setup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ temp_token: tempToken }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || '2FA setup failed');
    }
    return await res.json();
  }, []);

  const verify2FA = useCallback(async (email, code, tempToken) => {
    const res = await fetch(`${API}/api/auth/2fa/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, code, temp_token: tempToken }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const detail = err.detail;
      const e = new Error(typeof detail === 'object' ? detail.message : (detail || '2FA verification failed'));
      e.detail = detail;
      e.status = res.status;
      throw e;
    }
    const data = await res.json();
    if (data.session_token) {
      localStorage.setItem('greencad_token', data.session_token);
    }
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    const token = localStorage.getItem('greencad_token');
    try {
      await fetch(`${API}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
    } catch {}
    localStorage.removeItem('greencad_token');
    setUser(null);
  }, []);

  const initialSetup = useCallback(async (name, email, password) => {
    const res = await fetch(`${API}/api/auth/setup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name, email, password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Setup failed');
    }
    const data = await res.json();
    if (data.session_token) {
      localStorage.setItem('greencad_token', data.session_token);
    }
    setNeedsSetup(false);
    setUser({ user_id: data.user_id, email: data.email, name: data.name, role: data.role });
    return data;
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, needsSetup, loginStep1, setup2FA, verify2FA, logout, checkAuth, initialSetup }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
